import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationEntity } from '../entities';

// Seconds under which we show "Now" instead of "1 min ago".
const NOW_THRESHOLD_SEC = 45;

// The backend only sends `type` + params, so all human-readable strings live here.
export const useNotificationDisplay = () => {
  const { t } = useTranslation();

  const getTitle = useCallback(
    (notification: NotificationEntity): string =>
      notification.droneName?.trim() ||
      notification.droneSerial?.trim() ||
      t('notifications.fallbackDrone'),
    [t],
  );


  const getMessage = useCallback(
    (notification: NotificationEntity): string =>
      t(`notifications.types.${notification.type}`, {
        defaultValue: t('notifications.unknownType'),
      }),
    [t],
  );

  const getTime = useCallback(
    (isoDate: string): string => {
      const then = new Date(isoDate).getTime();
      if (Number.isNaN(then)) return '';

      const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (diffSec < NOW_THRESHOLD_SEC) return t('notifications.time.now');

      const minutes = Math.floor(diffSec / 60);
      if (minutes < 60) return t('notifications.time.minutes', { count: minutes });

      const hours = Math.floor(minutes / 60);
      if (hours < 24) return t('notifications.time.hours', { count: hours });

      const days = Math.floor(hours / 24);
      if (days === 1) return t('notifications.time.yesterday');
      if (days < 7) return t('notifications.time.days', { count: days });

      return new Date(isoDate).toLocaleDateString();
    },
    [t],
  );

  return { getTitle, getMessage, getTime };
};
